import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { NO_INSETS, type Insets } from "./useFitToRoom";

/** Insets written as CSS lengths, so they can reference layout vars like
 *  `var(--topbar-h)` that change with breakpoints and safe areas. */
export type CssInsets = Partial<Record<keyof Insets, string>>;

/**
 * Resolve CSS-length insets to pixels. Returns hidden probe elements to
 * render inside the measured container; ResizeObservers on them pick up
 * every change to the underlying vars (breakpoint flips, orientation, the
 * coach bar publishing `--bottom-stack-h`).
 */
export function useCssInsets(spec: CssInsets | undefined): { insets: Insets; probes: ReactNode } {
  const [insets, setInsets] = useState<Insets>(NO_INSETS);
  const startRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const enabled = spec != null;

  useLayoutEffect(() => {
    const a = startRef.current;
    const b = endRef.current;
    if (!enabled || !a || !b) return;
    const read = () => {
      const next = {
        left: a.offsetWidth,
        top: a.offsetHeight,
        right: b.offsetWidth,
        bottom: b.offsetHeight,
      };
      setInsets((prev) =>
        prev.top === next.top &&
        prev.right === next.right &&
        prev.bottom === next.bottom &&
        prev.left === next.left
          ? prev
          : next
      );
    };
    read();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    ro?.observe(a);
    ro?.observe(b);
    return () => ro?.disconnect();
  }, [enabled, spec?.top, spec?.right, spec?.bottom, spec?.left]);

  const probe = "pointer-events-none invisible absolute left-0 top-0";
  const probes = enabled ? (
    <>
      <div
        ref={startRef}
        aria-hidden
        className={probe}
        style={{ width: spec.left ?? "0px", height: spec.top ?? "0px" }}
      />
      <div
        ref={endRef}
        aria-hidden
        className={probe}
        style={{ width: spec.right ?? "0px", height: spec.bottom ?? "0px" }}
      />
    </>
  ) : null;

  return { insets: enabled ? insets : NO_INSETS, probes };
}
